import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { AddCircleOutline, MarkEmailRead, WorkspacePremium } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgExpandableRow from 'components/org-client/OrgExpandableRow';
import RequestPlanModal from 'components/org-client/RequestPlanModal';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import useLogger from 'hooks/useLogger';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { MY_ORGANIZATION, MY_ORGANIZATION_PLAN_REQUESTS } from 'api/graphql/orgClient';

const STATUS_TONE = {
  PENDING: { fg: 'sessionStatus.pending.fg', bg: 'sessionStatus.pending.bg', label: 'Pendiente' },
  APPROVED: { fg: 'sessionStatus.completed.fg', bg: 'sessionStatus.completed.bg', label: 'Aprobada' },
  REJECTED: { fg: 'sessionStatus.absent.fg', bg: 'sessionStatus.absent.bg', label: 'Rechazada' }
};

const formatDate = (iso) => {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return '--';
  }
};

/**
 * Planes de la empresa: lo que está habilitado hoy y el canal para pedir uno nuevo. La
 * activación siempre la hace un administrador de OTRIX; desde aquí solo se solicita.
 */
function OrgClientPlans() {
  const { t } = useTranslation(['orgClient']);
  const { logError } = useLogger();
  const pageTitle = t('menu.plans', { defaultValue: 'Planes' });
  useOrgPageTitle(pageTitle);

  const [modalOpen, setModalOpen] = useState(false);

  const { data: orgData, loading, error } = useQuery(MY_ORGANIZATION, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientPlans', queryError)
  });
  const { data: requestsData } = useQuery(MY_ORGANIZATION_PLAN_REQUESTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientPlans', queryError)
  });

  const organization = orgData?.myOrganization || null;
  const activePlans = useMemo(
    () => (organization?.plan_discounts || []).filter((plan) => plan.is_active),
    [organization]
  );
  const activePlanIds = useMemo(() => activePlans.map((plan) => String(plan.plan_id)), [activePlans]);
  const requests = useMemo(() => requestsData?.myOrganizationPlanRequests || [], [requestsData]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}>
        <Box sx={{ maxWidth: 560 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t('plans.title', { defaultValue: 'Planes habilitados para tu empresa' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('plans.subtitle', {
              defaultValue:
                'Estos son los planes que tus colaboradores pueden activar hoy, con el beneficio que cubre la empresa ya aplicado.'
            })}
          </Typography>
        </Box>
        <Button
          onClick={() => setModalOpen(true)}
          startIcon={<AddCircleOutline />}
          sx={{ ...premiumButtonSx, px: 3, flex: 'none' }}>
          {t('plans.requestCta', { defaultValue: 'Solicitar otro plan' })}
        </Button>
      </Stack>

      {error && !organization && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 2.5 }}>
          {t('errors.plans', { defaultValue: 'No pudimos cargar tus planes. Intenta de nuevo en un momento.' })}
        </Alert>
      )}

      {loading && !organization ? (
        <Grid container spacing={2}>
          {[0, 1, 2].map((index) => (
            <Grid key={index} xs={12} md={4}>
              <Skeleton variant="rounded" height={140} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {activePlans.length === 0 ? (
            <Grid xs={12}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 3,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'hairline.strong',
                  backgroundColor: 'surface.overlay'
                }}>
                <WorkspacePremium sx={{ color: 'text.disabled', fontSize: 32 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 1 }}>
                  {t('plans.emptyTitle', { defaultValue: 'Todavía no hay planes habilitados' })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 460, mx: 'auto' }}>
                  {t('plans.emptyBody', {
                    defaultValue:
                      'Pide el plan que necesitas y el equipo de OTRIX lo habilita para toda tu empresa.'
                  })}
                </Typography>
              </Box>
            </Grid>
          ) : (
            activePlans.map((plan) => (
              <Grid key={plan.plan_id} xs={12} sm={6} lg={4}>
                <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
                  <Stack spacing={1.25} sx={{ height: '100%' }}>
                    <WorkspacePremium sx={{ color: 'gold.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {plan.plan_name}
                    </Typography>
                    <Chip
                      size="small"
                      label={t('plans.discount', {
                        percent: plan.client_discount_percent,
                        defaultValue: 'La empresa cubre {{percent}}%'
                      })}
                      sx={{
                        alignSelf: 'flex-start',
                        fontWeight: 700,
                        color: 'gold.main',
                        backgroundColor: 'gold.soft'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('plans.discountHint', {
                        defaultValue: 'El descuento se aplica solo al pagar, sin cupones ni trámites.'
                      })}
                    </Typography>
                  </Stack>
                </PremiumCard>
              </Grid>
            ))
          )}
        </Grid>
      )}

      <Stack direction="row" spacing={1.25} sx={{ mt: 3, color: 'text.secondary' }}>
        <MarkEmailRead sx={{ fontSize: 18, color: 'gold.main', flex: 'none', mt: 0.25 }} />
        <Typography variant="caption">
          {t('plans.requestChannel', {
            defaultValue:
              'Cada solicitud le llega por correo y notificación a los administradores de OTRIX, que la revisan y te contactan para habilitar el plan.'
          })}
        </Typography>
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 3.5, mb: 1.5 }}>
        {t('plans.requestsTitle', { defaultValue: 'Tus solicitudes' })}
      </Typography>

      {requests.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('plans.requestsEmpty', { defaultValue: 'Todavía no has solicitado ningún plan.' })}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {requests.map((request) => {
            const tone = STATUS_TONE[request.status?.toUpperCase()] || STATUS_TONE.PENDING;

            return (
              <OrgExpandableRow
                key={request.id}
                title={request.plan?.name || t('plans.unknownPlan', { defaultValue: 'Plan solicitado' })}
                subtitle={t('plans.requestMeta', {
                  date: formatDate(request.created_at),
                  seats: request.seats,
                  defaultValue: '{{date}} · {{seats}} cupos'
                })}
                trailing={
                  <Chip
                    size="small"
                    label={t(`plans.status.${request.status?.toUpperCase()}`, { defaultValue: tone.label })}
                    sx={{ flex: 'none', fontWeight: 700, color: tone.fg, backgroundColor: tone.bg }}
                  />
                }>
                <Stack spacing={1} sx={{ pt: 1.5 }}>
                  {request.note && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {request.note}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {t('plans.requestedBy', {
                      name: request.requester?.full_name || '--',
                      defaultValue: 'Solicitado por {{name}}'
                    })}
                  </Typography>
                </Stack>
              </OrgExpandableRow>
            );
          })}
        </Stack>
      )}

      <RequestPlanModal open={modalOpen} onClose={() => setModalOpen(false)} activePlanIds={activePlanIds} />
    </Box>
  );
}

export default OrgClientPlans;
